import {FormattedMessage, useIntl} from "react-intl";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import '../scss/SurveyMessages.scss';
import {findTopLevelParent, shadeColor} from "../Formats";
import {Editor, EditorState, ContentState, Modifier, RichUtils, CompositeDecorator} from 'draft-js';
import 'draft-js/dist/Draft.css';
import {ReactComponent as OptionsIcon} from "../svg/options.svg";
import {ReactComponent as DeleteIcon} from "../svg/delete.svg";
import {ReactComponent as FreeTextIcon} from "../svg/speech-bubble.svg";
import {ReactComponent as SupportIcon} from "../svg/support.svg";
import {ReactComponent as ErrorIcon} from "../svg/error.svg";
import {ReactComponent as MessageIcon} from "../svg/message.svg";
import {ReactComponent as ConfirmIcon} from "../svg/confirm.svg";
import {ReactComponent as CancelIcon} from "../svg/cancel.svg";
import {ReactComponent as FollowUpIcon} from "../svg/followup.svg";
import {ReactComponent as TargetIcon} from "../svg/target.svg";
import {ReactComponent as VariableIcon} from "../svg/variable.svg";
import {ReactComponent as InfoIcon} from "../svg/snack-info.svg";
import {ReactComponent as AlertIcon} from "../svg/snack-warning.svg";
import {ReactComponent as ExpandIcon} from "../svg/expand.svg";
import {ReactComponent as CollapseIcon} from "../svg/collapse.svg";
import {ReactComponent as CloseIcon} from "../svg/close.svg";
import {ReactComponent as PlusIcon} from "../svg/plus.svg";
import {ReactComponent as BranchBrokenIcon} from "../svg/broken-link.svg"

import {
    Accordion,
    HoverTooltip,
    Popover,
    PopupElement,
    PopupProvider,
    SearchDropdown, Table, Tooltip, usePopup, WidthVaryingSelect
} from "../UIElements";
import {
    EditorFocusContext,
} from "./SurveyView";
import {
    checkIfParent,
    deleteKeyAndChildren,
    findUserById,
    generateNewBranchKey,
    generateNewOtherKey,
    getBranchFromCondition,
    getCategories,
    getFollowUpOptions,
    getNewCategoryKey,
    getNextBranchCondition,
    hasReplyAsChild,
    isCategoryUsedMoreThanOnce,
    removeCategoryKeyFromUserSurveyData,
    removeUnnecessaryKeys,
    sanitizeCondition,
    setNoFollowUpTypesToNull,
    setSupportTypeIfAbsent
} from "../DataFunctions";
import {CollapsedViewContext, CollapsedViewProvider, useDataContext} from "./SurveyContextProvider";
import {PostCodes} from "../api/APISurvey";
import {useSnackbar} from "../Content";
import {getErrorsByBranch} from "../SurveyDataErrorChecks";
import _ from "lodash";
import SaveResponse from "./surveyEditor/messagesTab/SaveResponse";

export default function SurveyMessages() {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')

    useEffect(() => {
        // Fix older survey config types without followups
        let updatedSurveyData = setNoFollowUpTypesToNull(surveyData)
        // Change old format condition to new format
        updatedSurveyData = sanitizeCondition(updatedSurveyData)
        // For old surveys, if no supporttype key, create one and set it as SUPPORTPROVIDER
        updatedSurveyData = setSupportTypeIfAbsent(updatedSurveyData)
        // Remove unnecessary keys if supportneed is not true
        updatedSurveyData = removeUnnecessaryKeys(updatedSurveyData)

        // Only update if there's a change to avoid infinite loops
        if (JSON.stringify(updatedSurveyData) !== JSON.stringify(surveyData)) {
            setSurveyData(updatedSurveyData)
        }
    }, [])

    return <PopupProvider>
        <CollapsedViewProvider>
            <MessageView config={surveyData?.config} />
        </CollapsedViewProvider>
        <PopupElement />
    </PopupProvider>
}

function MessageView({ config }) {
    const intl = useIntl()
    const { isCollapsed, setIsCollapsed } = useContext(CollapsedViewContext)

    const title = isCollapsed ? intl.formatMessage({id:'expandView', defaultMessage: 'Expand view'}) :
        intl.formatMessage({id:'collapseView', defaultMessage: 'Collapse view'})

    return <>
        <h4 className={"flex"}>
            {intl.formatMessage(
                {
                    id: 'survey.navigation.messages',
                    defaultMessage: 'Messages',
                })}
            <button title={title} className={`${isCollapsed ? "active" : ""} collapse-toggle non-draggable`} onClick={()=>setIsCollapsed(!isCollapsed)}>{isCollapsed ? <ExpandIcon /> : <CollapseIcon/> }</button>
        </h4>
        <div className={"messages node-wrapper initial"}>
            <FirstNode data={config} />
            <Tree data={config} />
        </div>
    </>
}

function Tree({ data }) {
    const parentBranchList = Object.keys(data).filter(key => key.startsWith("branch") && data[key].parent === null)
    const { value: surveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const renderNestedNodes = (parentKey, data) => {
        const children = Object.keys(data)
            .filter((key) => (key.startsWith("other") || key.startsWith("branch")) && data[key].parent === parentKey)
            .sort((a, b) => {
                // Put keys starting with "other" last
                if (a.startsWith("other")) return 1
                if (b.startsWith("other")) return -1

                // Extract number from branch name for sorting
                const extractNumber = (str) => {
                    const match = str.match(/(\D*)(\d+)/);
                    if (match) {
                        return {
                            prefix: match[1].toLowerCase(),
                            num: parseInt(match[2], 10),
                        }
                    }
                    return { prefix: str.toLowerCase(), num: -1 }
                }

                let conditionA = data[a]?.condition ? data[a]?.condition.replace(/^\^|\\b/g, '') : ''
                let conditionB = data[b]?.condition ? data[b]?.condition.replace(/^\^|\\b/g, '') : ''

                const numA = extractNumber(conditionA)
                const numB = extractNumber(conditionB)

                // Sort by branch prefix
                if (numA.prefix < numB.prefix) {
                    return -1
                } else if (numA.prefix > numB.prefix) {
                    return 1
                }

                // Sort by branch number
                return numA.num - numB.num
            })
            .map((key) => {
                const nestedChildren = renderNestedNodes(key, data)
                const otherBranch = key.startsWith("other")
                const replyBranch = data[key]?.condition === '.*'
                const showChildren = !otherBranch && !(surveyFinished && nestedChildren.length < 1)
                const hideChildren = surveyFinished && nestedChildren.length < 1

                if (otherBranch) {
                    return null
                }

                return (
                    <div key={key} className={`node-wrapper ${key}`}>
                        <Node id={key} data={data[key]} config={data} parentBranchList={parentBranchList}/>
                        <RenderBranchError branch={key} />
                        {showChildren &&
                            <div className="children">
                                {nestedChildren.length > 0 && nestedChildren}
                                {!hasReplyAsChild(data, key) && !surveyFinished && <AddNewBranch data={data} parent={key} parentBranchList={parentBranchList} />}
                            </div>
                        }
                    </div>
                )
            })

        return children
    }

    return <>
        <RenderBranchError branch={"initial"} />
        <div className="tree children">
            {renderNestedNodes(null, data)}
            {!hasReplyAsChild(data, null) && !surveyFinished && <AddNewBranch data={data} parent={null} parentBranchList={parentBranchList} />}
        </div>
    </>
}

export function RenderBranchError({branch}) {
    const intl = useIntl()
    const [showPopover, setShowPopover] = useState(false)
    const [falseNegative, setFalseNegative] = useState(false)
    const [dismissedErrors, setDismissedErrors] = useState({})
    const { value: branchOptionErrors, setValue: setBranchOptionErrors } = useDataContext('branchOptionErrors')
    const branchErrors = getErrorsByBranch(branchOptionErrors, branch)
    const hasErrors = !Object.keys(branchErrors).every(key => Array.isArray(branchErrors[key]) && branchErrors[key].length === 0 )

    useEffect(()=>{
        if (!_.isEqual(dismissedErrors, branchErrors)) {
            setFalseNegative(false)
        }
    },[branchOptionErrors])

    const dismissAlert = () => {
        setFalseNegative(true)
        setShowPopover(false)
        setDismissedErrors(branchErrors)
    }

    if (!hasErrors || falseNegative) {
        return null
    }

    const missingChildErrors = branchErrors?.missingChildErrors || []
    const missingOptionsErrors = branchErrors?.missingOptionsErrors || []

    return <div className={"branch-error-container"}>
        <button className={"branch-error-toggle"} onClick={()=>setShowPopover(!showPopover)}>
        <BranchBrokenIcon />
    </button>
        {showPopover &&
            <Popover closePopover={()=>setShowPopover(false)} showCloseButton={true}>
                <div>
                    <h4>
                        {intl.formatMessage({
                            defaultMessage:'Possible error detected:',
                            id:'possibleBranchError.header'
                        })}
                    </h4>
                    <p>
                        {intl.formatMessage({
                            defaultMessage:'It appears that your answer options may not align with the linked branches. Please review and edit if necessary.',
                            id:'possibleBranchError.desc'
                        })}
                    </p>
                    {missingChildErrors?.length > 0 &&
                        <>
                            <p>
                                <b>
                                    {intl.formatMessage({
                                        defaultMessage:'Answer options missing a linked branch',
                                        id:'missingBranch.header'
                                    })}
                                </b>
                            </p>
                            <ul>
                                {missingChildErrors.map((branch, i) => {
                                    return <li key={i}>{branch?.option}</li>
                                })}
                            </ul>
                        </>
                    }
                    {missingOptionsErrors?.length > 0 &&
                        <>
                            <p>
                                <b>
                                    {intl.formatMessage({
                                        defaultMessage:'Branches not referenced in answer options',
                                        id:'missingAnswerOption.header'
                                    })}
                                </b>
                            </p>
                            <ul>
                                {missingOptionsErrors.map((branch, i) => {
                                    return <li key={i}>{branch?.option}</li>
                                })}
                            </ul>
                        </>
                    }
                </div>
                <button onClick={()=>dismissAlert()}>
                    {intl.formatMessage({
                        defaultMessage:'Dismiss alert',
                        id:'falseNegative.button'
                    })}
                </button>
            </Popover>
        }
    </div>
}

function AddNewBranch({ data, parent, parentBranchList }) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const [showPopover, setShowPopover] = useState(false)
    const hasSiblings = checkIfParent(data, parent)
    const nextBranchCondition = getNextBranchCondition(data, parent)
    const { value: followUpConfigData } = useDataContext('followUpConfigData')
    const followUpOptions = getFollowUpOptions(followUpConfigData)
    const defaultFollowup = followUpOptions?.length > 0 ? followUpOptions[0] : null
    const nextBranch = {
        category: null,
        condition: `^${nextBranchCondition}\\b`,
        followuptype: defaultFollowup,
        supportneed: false,
        supporttype: 'TEACHER',
        message: '',
        parent: parent,
    }
    const nextReply = {
        condition: '.*',
        message: '',
        parent: parent,
    }
    const nextOther = {
        message: '',
        parent: parent,
    }

    const addBranch = (type) => {
        const newBranchKey = generateNewBranchKey(data)
        if (type === 'branch') {
            const newSurveyData = {...surveyData}
            newSurveyData.config[newBranchKey] = nextBranch
            if (!hasSiblings) {
                const nextOtherKey = generateNewOtherKey(data)
                newSurveyData.config[nextOtherKey] = nextOther
            }
            setSurveyData(newSurveyData)
        }
        if (type === 'reply') {
            const newSurveyData = {...surveyData}
            newSurveyData.config[newBranchKey] = nextReply
            setSurveyData(newSurveyData)
        }
        setShowPopover(false)
    }

    const addBranchOrOpenPopover = () => {
        if (hasSiblings) {
            addBranch('branch')
        } else {
            setShowPopover(true)
        }
    }


    return <div className={"add-branch-container"}>
        <button className={"add-branch non-draggable"} onClick={addBranchOrOpenPopover}>
            <PlusIcon />
        </button>
        {showPopover &&
            <Popover closePopover={()=>setShowPopover(false)}>
                <AddBranchPopover nextBranchKey={nextBranchCondition} addBranch={addBranch} data={data} parent={parent} parentBranchList={parentBranchList} />
            </Popover>
        }
    </div>
}

function AddBranchPopover({ addBranch, nextBranchKey, data, parent, parentBranchList}) {
    const intl = useIntl()
    const nodeColor = parent ? getNodeColor(parent, false, data, parentBranchList, true) :
        getNodeColor(`branch${nextBranchKey.toUpperCase()}`, false, data, [...parentBranchList, `branch${nextBranchKey.toUpperCase()}`], true)

    const backgroundColor = parent ? shadeColor(nodeColor, -10) : nodeColor

    return <div className={"add-branch-content"}>
        <button onClick={()=>addBranch('branch')}>
            <div className={"icon"} style={{backgroundColor: backgroundColor}}>
                {nextBranchKey.toUpperCase()}
            </div>
            <span>
                {intl.formatMessage(
                    {
                        id: 'survey.message.option',
                        defaultMessage: 'Multiple choice option',
                    })}
            </span>
        </button>
        <button onClick={()=>addBranch('reply')}>
            <div className={"icon"} style={{backgroundColor: backgroundColor}}>
            <FreeTextIcon />
            </div>
            <span>
                {intl.formatMessage(
                {
                    id: 'survey.message.freeText',
                    defaultMessage: 'Free text response',
                })}
            </span>
        </button>

    </div>
}

const NodeActiveContext = React.createContext(null)

function NodeFocusState({ id, children }) {
    const [nodeIsActive, setNodeIsActive] = useState(false)
    const node = useRef(null)

    useEffect(() => {
        const handleClickInside = (event) => {
            if (node.current.contains(event.target)) {
                setNodeIsActive(true)
            }
        }

        const handleClickOutside = (event) => {
            if (node.current && !node.current.contains(event.target)) {
                setNodeIsActive(false);
            }
        }

        document.addEventListener('mousedown', handleClickInside)
        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickInside)
            document.removeEventListener('mousedown', handleClickOutside)
        };
    }, []);

    return (
        <NodeActiveContext.Provider value={{ nodeIsActive, setNodeIsActive }}>
        <div className={`${nodeIsActive ? "focused" : ""} node ${id} non-draggable`} ref={node}>
            {children}
        </div>
        </NodeActiveContext.Provider>
    )
}

function FirstNode({ data }) {
    const intl = useIntl()
    const placeholderText = intl.formatMessage({
        id: 'survey.message.placeholder.first',
        defaultMessage: 'This is the first message to the student! Click to type, and maybe start with a greeting? :)',
    })

    return (
        <NodeFocusState id={'initial'}>
            <div className="node-content">
                <div className={"header"}>
                    <MessageIcon />
                    {intl.formatMessage({id:'survey.firstMessage',defaultMessage:'First message'})}
                </div>
                <MessageEditor text={data.message} keyName={"initial"} placeholder={placeholderText} />
                <div className={"toolbar"}>
                    <SaveResponse branchData={data} branchKey={"initial"}/>
                </div>
            </div>
        </NodeFocusState>
    )
}

function getNodeColor( id, data, config, parentBranchList, isBranch ) {
    const colors = ["#8E99E4", "#FF9650", "#FFCE00", "#FD779F", "#007E71", "#00469A", "#FFCAA7", "#ABD4CC"]

    //console.log(id, config, parentBranchList)

    if (!isBranch) {
        return "#ADADAD"
    } else {
        let color = getColorForBranch(id, config, parentBranchList, colors);
        return color || "#ADADAD"
    }
}

function getColorForBranch(id, config, parentBranchList, colors, recursionDepth = 0) {
    if (parentBranchList.includes(id)) {
        const position = parentBranchList.indexOf(id)
        let color = colors[position % colors.length]
        return shadeColor(color, recursionDepth * -10) // 10% darker for each recursion level
    } else if (config[id] && config[id].parent !== null) {
        // If branch is not a top-level branch, check its parent
        return getColorForBranch(config[id].parent, config, parentBranchList, colors, recursionDepth + 1)
    } else {
        return null
    }
}

export function getNodeColorForNested(config, branchKey, isBranch) {
    const colors = ["#8E99E4", "#FF9650", "#FFCE00", "#FD779F", "#007E71", "#00469A", "#FFCAA7", "#ABD4CC"]
    const topLevelBranches = Object.keys(config).filter(key => key.startsWith('branch'))

    let color = "#ADADAD"

    if (isBranch) {
        let position;
        if (topLevelBranches.includes(branchKey)) {
            position = topLevelBranches.indexOf(branchKey)
            color = (position >= 0) ? colors[position % colors.length] : color
        } else {
            const parentObj = findTopLevelParent(config, branchKey)
            if (parentObj) {
                position = topLevelBranches.indexOf(parentObj.parent)
                if (position >= 0) {
                    const parentColor = colors[position % colors.length]
                    color = shadeColor(parentColor, parentObj.depth * -10)
                } else {
                    console.error(`Parent branch ${parentObj.parent} not found in topLevelBranches`)
                }
            }
        }
    }

    return color
}


function Node({ id, data, config, parentBranchList }) {
    const intl = useIntl()
    const otherBranch = id.startsWith("other")
    const replyBranch = data?.condition === ".*"
    const isBranch = id.startsWith("branch")
    const noToolbar = otherBranch
    const branchEditor = !(otherBranch || replyBranch)
    const showOptions = isBranch
    let placeholderText = ""
    if (otherBranch) {
       placeholderText = intl.formatMessage({
           id: 'survey.message.placeholder.error',
           defaultMessage: 'Write an error message for mistyped answers',
       })
    } else if (replyBranch) {
        placeholderText =  intl.formatMessage({
            id: 'survey.message.placeholder.reply',
            defaultMessage: 'Give a finishing reply or a thank you for answering',
        })
    } else {
        placeholderText =  intl.formatMessage({
            id: 'survey.message.placeholder',
            defaultMessage: 'Click to type your message',
        })
    }

    const nodeColor = getNodeColor(id, data, config, parentBranchList, isBranch)

    if (otherBranch) {
        return null
    }

    return (
        <NodeFocusState id={id}>
            <Line />
            <div className="node-content" style={{backgroundColor: nodeColor}}>
                <BranchHeader data={data} keyName={id} otherBranch={otherBranch} replyBranch={replyBranch} branchEditor={branchEditor} showOptions={showOptions}/>
                <MessageEditor text={data?.message} keyName={id} placeholder={placeholderText} borderBottom={noToolbar} />
                {!noToolbar &&
                <Toolbar data={data} keyName={id}/>
                }
            </div>
        </NodeFocusState>
    )
}

function BranchHeader({ data, keyName, otherBranch, replyBranch, branchEditor, showOptions }) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const intl = useIntl()
    const renderIconAndText = () => {
        let branchText = ""
        let icon
        if (otherBranch) {
            branchText = intl.formatMessage({id: 'survey.message.other', defaultMessage: 'Error message'})
            icon = <ErrorIcon/>
        }
        if (replyBranch) {
            branchText = intl.formatMessage({id: 'survey.message.freeText', defaultMessage: 'Free text response'})
            icon = <FreeTextIcon/>
        }

        return <>
            {icon}
            {branchText}
        </>
    }

    return <div className="header">
        {branchEditor ? <BranchDisplay data={data} keyName={keyName} condition={data?.condition}/> :
            renderIconAndText()
        }
        {showOptions && <BranchOptions branchData={data} branchKey={keyName}/>}
    </div>
}

function BranchOptions({ branchData, branchKey }) {
    const intl = useIntl()
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const [showOptions, setShowOptions] = useState(false)
    const {showPopup} = usePopup()

    const deleteBranch = () => {
        const newSurveyData = {...surveyData}
        deleteKeyAndChildren(newSurveyData.config, branchKey)
        setSurveyData(newSurveyData)
        setShowOptions(false)
    }

    if (surveyFinished) {
        return null
    }

    return <div className={"branch-options popover-container"}>
        <button className={"toggle popover-toggle"} onClick={()=> setShowOptions(true)}>
            <OptionsIcon />
        </button>
        {showOptions &&
        <Popover closePopover={()=>setShowOptions(false)}>
            <button className={"delete"} onClick={() => deleteBranch()}>
                <DeleteIcon />
                {intl.formatMessage(
                    {
                        id: 'survey.message.deleteMessage',
                        defaultMessage: 'Delete Message',
                    })}
            </button>
        </Popover>
        }
</div>
}

function BranchDisplay({ data, keyName, condition }) {
    const intl = useIntl()
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const surveyConfig = surveyData?.config
    const originalBranchName = getBranchFromCondition(condition)
    const [branchName, setBranchName] = useState(originalBranchName)
    const [showConfirm, setShowConfirm] = useState(false)
    const [branchCheckFailed, setBranchCheckFailed] = useState(false)
    const [errorMessage, setErrorMessage] = useState("");
    const inputClass = branchCheckFailed ? "error" : showConfirm ? "active" : ""

    useEffect(() => {
        let error = ""
        if (branchName.length < 1) {
            error = intl.formatMessage({
                defaultMessage: "Option text empty",
                id:"branchOptionEmpty"
            })
            setBranchCheckFailed(false)
        } else if (!/^[a-zA-Z0-9äöåÄÖÅæøÆØ]*$/.test(branchName)) {
            error = intl.formatMessage({
                defaultMessage: "No special characters allowed",
                id:"specialCharactersNotAllowed"
            })
            setBranchCheckFailed(true)
        } else if (!branchConditionIsUnique()) {
            error = intl.formatMessage({
                defaultMessage: "Given option is already taken",
                id:"optionAlreadyTaken"
            })
            setBranchCheckFailed(true)
        } else {
            setBranchCheckFailed(false)
        }
        setErrorMessage(error)
    }, [branchName])

    useEffect(()=>{
        setBranchName(originalBranchName)
    },[originalBranchName])

    function branchConditionIsUnique() {
        const targetParent = surveyConfig[keyName]?.parent
        const condition = `^${branchName.toLowerCase()}\\b`

        for (const key in surveyConfig) {
            if (key.startsWith('branch') && key !== keyName) {
                const branch = surveyConfig[key]
                if (branch.parent === targetParent && branch.condition === condition) {
                    return false
                }
            }
        }

        return true
    }

    const changeBranch = e => {
        e.preventDefault()
        const newCondition = `^${branchName.toLowerCase()}\\b`
        const newSurveyData = {...surveyData}
        surveyData.config[keyName].condition = newCondition
        setSurveyData(newSurveyData)
        setShowConfirm(false)
    }

    if (surveyFinished) {
        return <form className={"branch-editor disabled"}>
            <input type={"text"} disabled value={branchName.toUpperCase()}/>
        </form>
    }

    return <form className={"branch-editor"} onSubmit={branchCheckFailed ? null : changeBranch}>
        <input type={"text"}
               title={errorMessage?.length > 0 ? errorMessage: ""}
               className={inputClass}
               value={branchName.toUpperCase()}
               onChange={e => {
                   setBranchName(e.target.value || "")
                   setShowConfirm(true)
               }}
        />
        {showConfirm &&
        <>
            <button className={"cancel"} title={"cancel"} onClick={()=>{
                setBranchName(originalBranchName)
                setShowConfirm(false)
                setErrorMessage("")
            }}>
                <CancelIcon />
            </button>
            {branchCheckFailed || branchName?.length < 1 ?
                <button className={"confirm disabled"} disabled={true} title={"confirm"}>
                    <ConfirmIcon />
                </button> :
            <button className={"confirm"} type={"submit"} title={"confirm"}>
                <ConfirmIcon />
            </button>
            }
        </>
        }
        {branchCheckFailed && <div className="error-message">{errorMessage}</div>}
    </form>
}

function Toolbar({ data, keyName }) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { value: userSurveyData, setValue: setUserSurveyData } = useDataContext('userSurveyData')
    const { value: userData } = useDataContext('userData')
    const { value: categoryData } = useDataContext('categoryData')
    const { value: followUpConfigData } = useDataContext('followUpConfigData')
    const { value: supportRolesData } = useDataContext('supportRolesData')
    const { nodeIsActive } = useContext(NodeActiveContext)
    const { isCollapsed } = useContext(CollapsedViewContext)
    const { showPopup } = usePopup()

    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const limitedEditing = surveyData.status === "IN PROGRESS" || surveyData.status === "FINISHED"
    const intl = useIntl()

    const supportNeedActive = data?.supportneed

    const config = surveyData?.config
    const ancestorHasSupportNeed = (config, key) => {
        const parentKey = config[key]?.parent
        return parentKey ? config[parentKey]?.supportneed === true || ancestorHasSupportNeed(config, parentKey) : false
    }
    const parentHasSupportNeed = config[keyName]?.parent ? config[config[keyName].parent]?.supportneed === true || ancestorHasSupportNeed(config, config[keyName].parent) : false
    const grandChildrenHasSupportNeed = (config, key) => Object.values(config).some(child => child.parent === key && (child.supportneed === true || grandChildrenHasSupportNeed(config, Object.keys(config).find(childKey => config[childKey] === child))));
    const childHasSupportNeed = Object.values(config).some(child => child.parent === keyName && (child.supportneed === true || grandChildrenHasSupportNeed(config, Object.keys(config).find(childKey => config[childKey] === child))));

    const supportCategorySet = data?.category && categoryData.hasOwnProperty(data?.category)
    const supportCategoryName = supportCategorySet ? categoryData[data?.category]?.title
        : intl.formatMessage({
        id: 'survey.chooseSupportNeed',
        defaultMessage: '+ Choose Support Topic',
    })

    const toggleSupportNeed = (status) => {
        // If support need removed with supporttype SUPPORTPROVIDER, annieusersurvey needs to be cleaned
        if (!status && data?.supporttype === 'SUPPORTPROVIDER' && data.hasOwnProperty('category') && !isCategoryUsedMoreThanOnce(data?.category, surveyData?.config)) {
            const newUserSurveyData = removeCategoryKeyFromUserSurveyData(userSurveyData, data?.category)
            setUserSurveyData(newUserSurveyData)
        }

        const newSurveyData = {...surveyData}
        newSurveyData.config[keyName].supportneed = status
        setSurveyData(newSurveyData)
    }

    return <div className={`${isCollapsed ? "collapsed" : ""} toolbar`}>
        {isCollapsed ?
            <div className={"selection-container fill no-supportneed"}>
                <SupportIcon/>
                <div className={"selections"}>
                    <div>
                        <h5>
                            {intl.formatMessage({
                                id: 'supportNeed',
                                defaultMessage: 'Support need',
                            })}
                        </h5>
                        {surveyFinished ?
                            <input type={"checkbox"} disabled readOnly={true} className={"switch"} id={`supportneed-${keyName}`} defaultChecked={supportNeedActive}/>
                        :
                            <input type={"checkbox"} onChange={()=>toggleSupportNeed(!supportNeedActive)} className={"switch"} id={`supportneed-${keyName}`} defaultChecked={supportNeedActive}/>
                        }
                        <label htmlFor={`supportneed-${keyName}`} className={"switch-label"} />
                    </div>
                </div>
            </div> :
            <>
                <div className={`${supportNeedActive ? "" : "no-supportneed"} selection-container fill`}>
                    <SupportIcon/>
                    <div className={"selections"}>
                        <div>
                            <h5>
                                {intl.formatMessage({
                                    id: 'supportNeed',
                                    defaultMessage: 'Support need',
                                })}
                            </h5>
                            {surveyFinished ?
                                <input type={"checkbox"} disabled readOnly={true} className={"switch"} id={`supportneed-${keyName}`} defaultChecked={supportNeedActive}/>
                                : (parentHasSupportNeed && !supportNeedActive) ?
                                    <HoverTooltip text={intl.formatMessage({id:'parentHasSupportNeed.error', defaultMessage:'The input is disable because the parent branch already creates a support request.'})}>
                                        <input type={"checkbox"} disabled readOnly={true} className={"switch"} id={`supportneed-${keyName}`} defaultChecked={supportNeedActive}/>
                                        <label htmlFor={`supportneed-${keyName}`} className={"switch-label"} />
                                    </HoverTooltip> :
                                    (childHasSupportNeed && !supportNeedActive) ?
                                        <HoverTooltip text={intl.formatMessage({id:'childHasSupportNeed.error', defaultMessage:'The input is disable because at least one of the branch children creates a support request.'})}>
                                            <input type={"checkbox"} disabled readOnly={true} className={"switch"} id={`supportneed-${keyName}`} defaultChecked={supportNeedActive}/>
                                            <label htmlFor={`supportneed-${keyName}`} className={"switch-label"} />
                                        </HoverTooltip> :
                                <input type={"checkbox"} onChange={()=>toggleSupportNeed(!supportNeedActive)} className={"switch"} id={`supportneed-${keyName}`} defaultChecked={supportNeedActive}/>
                            }
                            {(surveyFinished || supportNeedActive || (!parentHasSupportNeed && !childHasSupportNeed)) &&
                                <label htmlFor={`supportneed-${keyName}`} className={"switch-label"} />
                            }
                        </div>
                        {supportNeedActive &&
                            <div>
                                <h5>
                                    {intl.formatMessage({
                                        id: 'supportTopic',
                                        defaultMessage: 'Support topic',
                                    })}
                                </h5>
                                {surveyFinished ?
                                    <button className={`${supportCategorySet ? "" : "unset"}`} disabled>
                                        {supportCategoryName}
                                    </button> :
                                    <button className={`${supportCategorySet ? "" : "unset"}`} onClick={() => showPopup(<CategorySelector branchData={data} branchKey={keyName} />, 'full-width')}>
                                        {supportCategoryName}
                                    </button>
                                }
                            </div>
                        }
                    </div>
                </div>
                {supportNeedActive &&
                    <>
                        <div className={"selection-container stroke"}>
                            <TargetIcon/>
                            <SupportTypeSelect branchData={data} branchKey={keyName} supportCategorySet={supportCategorySet} supportCategoryName={supportCategoryName}/>
                        </div>
                        <FollowUpSelector branchData={data} branchKey={keyName} />
                    </>
                }
            </>
        }
        <SaveResponse branchData={data} branchKey={keyName} />
    </div>
}

function FollowUpSelector({ branchData, branchKey }) {
    const intl = useIntl()
    const { value: followUpConfigData } = useDataContext('followUpConfigData')
    const followUpOptions = getFollowUpOptions(followUpConfigData)
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const activeFollowUp = branchData?.followuptype ? branchData?.followuptype : "NOFOLLOWUP"

    const setUsedFollowUp = (followup) => {
        const followupType = followup === "NOFOLLOWUP" ? null : followup
        const newSurveyData = {...surveyData}
        newSurveyData.config[branchKey].followuptype = followupType
        setSurveyData(newSurveyData)
    }

    const followUpOptionsAndNoFollowup = followUpOptions.concat("NOFOLLOWUP")
    // Check if followup is not in use anymore
    const followUpIsNotInOptions = !followUpOptionsAndNoFollowup.includes(activeFollowUp)

    return <div className={"selection-container"}>
        <FollowUpIcon />
        <div className={"selections"}>
            <div>
                <h5>
                    {intl.formatMessage({id:"survey.followUp",defaultMessage:"Follow up"})}
                    <Tooltip text={intl.formatMessage({id:'followUpTooltip', defaultMessage:'Select a follow up survey type. It will automatically check in with students on their support experience 2 weeks after they have requested support.'})} />
                </h5>
                <WidthVaryingSelect
                    options={followUpOptionsAndNoFollowup.concat(followUpIsNotInOptions ? [activeFollowUp] : [])}
                    value={activeFollowUp}
                    onChange={setUsedFollowUp}
                    className={followUpIsNotInOptions ? "alert" : ""}
                    disabledOptions={followUpIsNotInOptions ? [activeFollowUp] : []}
                    optionTexts={(option) => {
                        if (option === 'NOFOLLOWUP') {
                            return intl.formatMessage({
                                id: "followup.noFollowUp",
                                defaultMessage: "No follow up"
                            });
                        } else if (followUpOptions.includes(option)) {
                            return intl.formatMessage({
                                id: "followup."+option,
                                defaultMessage: option
                            })
                        } else {
                            return option
                        }}}
                />
            </div>
            {followUpIsNotInOptions &&
                <p className={"info alert"}>
                    <AlertIcon />
                    <span className={"text"}>
                    {intl.formatMessage({id:"followUpIsNotInUse",defaultMessage:"The chosen followup is not in use. Please select another."})}
                </span>
                </p>
            }
        </div>
    </div>
}

function CategorySelector({ branchData, branchKey }) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { value: userSurveyData, setValue: setUserSurveyData } = useDataContext('userSurveyData')
    const { value: categoryData } = useDataContext('categoryData')
    const { hidePopup } = usePopup()
    const option = getBranchFromCondition(branchData?.condition)
    const [searchText, setSearchText] = useState("")
    const intl = useIntl()
    const branchCategory = branchData?.category

    const usedCategories = getCategories(surveyData?.config)

    const themes = useMemo(() => {
        return Object.entries(categoryData).reduce((acc, [key, item]) => {
            const { theme } = item
            if (!acc[theme]) {
                acc[theme] = []
            }
            acc[theme].push({key, ...item})
            return acc
        }, {})
    }, [categoryData])

    const filteredThemes = useMemo(() => {
        if (!searchText) return themes
        const lowerSearchText = searchText.toLowerCase()
        return Object.entries(themes).reduce((acc, [theme, items]) => {
            const filteredItems = items.filter(item =>
                item.title.toLowerCase().includes(lowerSearchText) ||
                intl.formatMessage({id:'theme.'+theme, defaultMessage:'theme'}).toLowerCase().includes(lowerSearchText)
            )
            if (filteredItems.length > 0) {
                acc[theme] = filteredItems
            }
            return acc
        }, {})
    }, [searchText, themes])

    const isMatch = useMemo(() => {
        if (!searchText) return true
        const lowerSearchText = searchText.toLowerCase()
        return Object.values(categoryData).reduce((acc, item) => {
            const { theme, title } = item
            if (title.toLowerCase().includes(lowerSearchText)) {
                acc[theme] = true
            }
            return acc
        }, {})
    }, [searchText, categoryData])

    const onItemClick = (item) => {
        // If support topic changed with supporttype SUPPORTPROVIDER, annieusersurvey needs to be cleaned
        if (branchData?.supporttype === 'SUPPORTPROVIDER' && branchData.hasOwnProperty('category') && !isCategoryUsedMoreThanOnce(branchCategory, surveyData?.config)) {
            const newUserSurveyData = removeCategoryKeyFromUserSurveyData(userSurveyData, branchCategory)
            setUserSurveyData(newUserSurveyData)
        }

        const newSurveyData = {...surveyData}
        newSurveyData.config[branchKey].category = item
        setSurveyData(newSurveyData)
        hidePopup()
    }

    return <div className={"category-selection"}>
        <h1>
            {intl.formatMessage({
                id: 'survey.assignNeedForAnswer',
                defaultMessage: 'Choose Support Topic for Option',
            })}
            <b> {option}</b>
        </h1>
        <input type={"text"} className={"search"} value={searchText}
               autoFocus
               onChange={e => {
                   setSearchText(e.target.value)
               }}
               placeholder={intl.formatMessage(
                   {
                       id: 'codes.search.placeholder',
                       defaultMessage: 'Search for Categories',
                   })}/>
        <div className={"accordion-container"}>
            {Object.entries(filteredThemes).map(([theme, items]) => (
                <Accordion key={theme}
                           themeKey={theme}
                           theme={theme !== "undefined" ? intl.formatMessage({id:'theme.'+theme, defaultMessage:theme}) : intl.formatMessage({id:'missingTheme', defaultMessage:"Missing theme"})}
                           items={items}
                           onItemClick={onItemClick}
                           isOpen={isMatch[theme]}
                           activeKey={branchCategory}
                           usedCategories={usedCategories}
                />
            ))}
        </div>
    </div>
}

export function AddNewTopic({ onItemClick, theme }) {
    const { value: categoryData, setValue: setCategoryData } = useDataContext('categoryData')
    const [showInput, setShowInput] = useState(false)
    const [categoryTitle, setCategoryTitle] = useState("")
    const postCode = PostCodes()
    const {setSnackbarData} = useSnackbar()

    const handleSubmit = (e) => {
        e.preventDefault()
        if (categoryTitle?.length < 1) return

        const newKey = getNewCategoryKey(categoryData)
        const newCodeValue = {
            "theme": theme,
            "title": categoryTitle.trim(),
        }
        const codeToPost = [{
                codeset: 'category',
                code: newKey,
                value: newCodeValue,
            }]

        postCode.mutate(codeToPost, {
            onError: error => {
                console.log(error)
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'addNewTopic.error',
                            defaultMessage: 'Adding a new topic failed. Please try again.',
                        }),
                    state: "error",
                    autoHide: true,
                })
            },
            onSuccess: () => {
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'addNewTopic.success',
                            defaultMessage: 'New topic added',
                        }),
                    state: "success",
                    autoHide: true,
                })
                const newCategoryData = { ...categoryData }
                newCategoryData[newKey] = newCodeValue
                setCategoryData(newCategoryData)

                onItemClick(newKey)
            }
        })
    }

    const intl = useIntl()
    return <>
    {showInput ?
        <form className={"category-input"} onSubmit={handleSubmit}>
            <input type={"text"} autoFocus value={categoryTitle} onChange={e => setCategoryTitle(e.target.value)} />
            <input type={"submit"} value={intl.formatMessage({
                id: 'addTopic',
                defaultMessage: '+ Add Topic',
            })} />
        </form> :
    <div className={"item add-new"} onClick={() => setShowInput(true)}>
        {intl.formatMessage({
            id: 'addTopic',
            defaultMessage: '+ Add Topic',
        })}
    </div>
    }
        </>
}

function SupportTypeSelect({ branchData, branchKey, supportCategorySet, supportCategoryName }) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { value: supportRolesData } = useDataContext('supportRolesData')
    const { value: userSurveyData, setValue: setUserSurveyData } = useDataContext('userSurveyData')
    const defaultRole = supportRolesData?.length ? supportRolesData[0] : "SPED"
    const getSupportType = (type) => {
        if (supportRolesData.includes(type) || type === "TEACHER") {
            return "ROLE"
        }
        return type
    }
    const supportType = branchData.hasOwnProperty("supporttype") ? getSupportType(branchData.supporttype) : "ROLE"
    const supportOptions = ["ROLE", "SUPPORTPROVIDER", "INFORMATION"]
    const roleOptions = [...supportRolesData]
    const roleType= branchData.hasOwnProperty("supporttype") && roleOptions.includes(branchData.supporttype) ? branchData.supporttype : defaultRole
    const category = branchData?.category
    const intl = useIntl()

    const setNewSupportType = (type) => {
        const newSurveyData = {...surveyData}
        newSurveyData.config[branchKey].supporttype = type
        setSurveyData(newSurveyData)
    }

    const handleSupportTypeChange = (value) => {
        // Check if previous value was SUPPORTPROVIDER and remove rights from annieusersurvey if not duplicate support topic
        if (supportType === 'SUPPORTPROVIDER' && !isCategoryUsedMoreThanOnce(category, surveyData?.config)) {
            const newUserSurveyData = removeCategoryKeyFromUserSurveyData(userSurveyData, category)
            setUserSurveyData(newUserSurveyData)
        }

        const supportTypeValue = value === "ROLE" ? defaultRole : value

        const newSurveyData = {...surveyData}
        newSurveyData.config[branchKey].supporttype = supportTypeValue
        setSurveyData(newSurveyData)
    }

    const handleRoleChange = (role) => {
        const newSurveyData = {...surveyData}
        newSurveyData.config[branchKey].supporttype = role
        setSurveyData(newSurveyData)
    }

    useEffect(()=>{
        if (surveyData.config[branchKey].supportneed) {
            const newSurveyData = {...surveyData}
            newSurveyData.config[branchKey].supporttype = roleType
            setSurveyData(newSurveyData)
        }
    },[])

    const displayDuplicateCategoryWarning = supportCategorySet && supportType === "SUPPORTPROVIDER" && isCategoryUsedMoreThanOnce(category, surveyData?.config)

    return <div className={"selections"}>
        <div>
            <h5>
                {intl.formatMessage({id:"assignTo",defaultMessage:"Assign to"})}
            </h5>
            <WidthVaryingSelect
                options={supportOptions}
                value={supportType}
                onChange={handleSupportTypeChange}
                optionTexts={(option) =>
                    intl.formatMessage({
                        id: "supporttype."+option,
                        defaultMessage: option
                    })
                }
            />
        </div>
        {supportType === "ROLE" &&
        <div>
            <h5>
                {intl.formatMessage({
                    id: 'selectRole',
                    defaultMessage: 'Select role',
                })}
            </h5>
            <WidthVaryingSelect
                options={roleOptions}
                value={roleType}
                onChange={handleRoleChange}
                optionTexts={(role) =>
                    intl.formatMessage({
                        id: "role."+role,
                        defaultMessage: role
                    })
                }
            />
        </div>
        }
        {supportType === "SUPPORTPROVIDER" &&
            <div>
                <h5>
                    {intl.formatMessage({
                        id: 'assignToPerson',
                        defaultMessage: 'Assign to Person',
                    })}
                </h5>
                {supportCategorySet ?
                    <ProviderSelection branchData={branchData} branchKey={branchKey} category={category} displayDuplicateCategoryWarning={displayDuplicateCategoryWarning} /> :
                    <button disabled>
                        {intl.formatMessage(
                        {
                            id: 'survey.supportNeeds.addSupportProvider.placeholder',
                            defaultMessage: 'Add support topic before assigning',
                        })}
                    </button>
                }
            </div>
        }
        {displayDuplicateCategoryWarning &&
            <p className={"info"}>
                <InfoIcon />
                <span className={"text"}>
                    {intl.formatMessage({
                        id:"duplicateCategoryWarning",
                        defaultMessage:"Assigned people will see all support needs with support topic:"
                    })} <b>{supportCategoryName}</b>
                </span>
            </p>
        }
    </div>
}

function ProviderSelection({ branchData, branchKey, category, displayDuplicateCategoryWarning }) {
    const intl = useIntl()
    const { value: userSurveyData } = useDataContext('userSurveyData')
    const { value: userData } = useDataContext('userData')
    const providers = userSurveyData.filter(obj => obj.hasOwnProperty('meta') && obj.meta.hasOwnProperty('category') && obj?.meta?.category[category])
    const assigned = providers?.length > 0
    const { showPopup, hidePopup } = usePopup()

    const providersText = `${findUserById(providers[0]?.annieuser, userData)}${providers?.length > 1 ? ` + ${providers?.length - 1}` : ''}`

    const buttonText =  assigned ? providersText : intl.formatMessage({
        id: 'survey.assign',
        defaultMessage: '+ Assign',
    })

    return <>
        <button className={assigned ? "" : "unset"} onClick={()=>showPopup(<ProviderSelector branchData={branchData} branchKey={branchKey} category={category} providers={providers}/>, 'max-width')}>{buttonText}</button>
        </>
}

function ProviderSelector({ category, providers }) {
    const intl = useIntl()
    const { value: surveyData } = useDataContext('surveyData')
    const { value: categoryData } = useDataContext('categoryData')
    const { value: userData } = useDataContext('userData')
    const { value: userSurveyData, setValue: setUserSurveyData } = useDataContext('userSurveyData')
    const { value: copiedUsersData, setValue: setCopiedUsersData } = useDataContext('copiedUsersData')
    const initialSelections = providers?.length > 0 ? providers.map(user => user?.annieuser) : []
    const [selectedProviders, setSelectedProviders] = useState(initialSelections)
    const {setSnackbarData} = useSnackbar()
    const {hidePopup} = usePopup()
    const topic = categoryData[category]?.title
    const columns = React.useMemo(()=>[
        {
            Header: () => (
                <span>
                    {intl.formatMessage({
                        defaultMessage:'Email',
                        id:'email'
                    })}
                    <i> {intl.formatMessage({
                        defaultMessage:'or id',
                        id:'orId'
                    })}</i>
                </span>
            ),
            accessor: 'meta.email',
            Cell: ({ cell: { value }, row: { original } }) => {
                if (value) {
                    return value
                } else {
                    return <i>{original?.meta?.userid || original.id}</i>
                }
            },
        },
        {
            Header: intl.formatMessage({defaultMessage:'Name',id:'name'}),
            id: 'name',
            accessor: row => `${row.meta.firstname || ''} ${row.meta.lastname || ''}`.trim(),
        },
        {
            Header: intl.formatMessage({defaultMessage:'Role',id:'role'}),
            accessor: 'meta.role',
        },
        {
            Header: intl.formatMessage({defaultMessage:'Aliases',id:'aliases'}),
            accessor: 'meta.aliases',
            Cell: ({value}) => {
                if (value === null || value === undefined) {
                    return ''
                }

                return value.map((user, i) => (
                    <React.Fragment key={i}>
                        {user}{i < value.length - 1 ? ', ' : ''}
                        {i < value.length - 1 && <br />}
                    </React.Fragment>
                ))
            }
        },
    ],[])
    const globalSearchPlaceholder = intl.formatMessage({id:'searchSupportProviders.placeholder', defaultMessage:'Search support providers'})

    const removeUserSelection = (user) => {
        setSelectedProviders((prevState) => prevState.filter((provider) => provider !== user))
    }

    const addUserSelection = (user) => {
        setSelectedProviders((prevState) => [...prevState, user])
    }

    const handleProviderUpdate = (id, state) => {
        if (!state) {
            removeUserSelection(id)
        } else {
            addUserSelection(id)
        }
    }

    const setUsersToCopy = () => {
        setCopiedUsersData(selectedProviders)
        setSnackbarData({
            show: true,
            text: intl.formatMessage(
                {
                    id: 'selectionsCopied',
                    defaultMessage: 'Selections copied',
                }),
            state: "info",
            autoHide: true,
        })
    }

    const pasteSelections = () => {
        setSelectedProviders(copiedUsersData)
        setSnackbarData({
            show: true,
            text: intl.formatMessage(
                {
                    id: 'selectionsPasted',
                    defaultMessage: 'Selections pasted',
                }),
            state: "info",
            autoHide: true,
        })
    }

    const clearSelections = () => {
        setSelectedProviders([])
    }

    const cancelProviderEdits = () => {
        hidePopup()
    }

    const confirmProviderEdits = () => {
        const selectionSet = new Set(selectedProviders)
        const userMap = new Map(userSurveyData.map(user => [user.annieuser, user]))

        for (let user of selectionSet) {
            if (userMap.has(user)) {
                const currentUserMeta = userMap.get(user).meta
                // Add category key if it doesn't exist
                if (!('category' in currentUserMeta)) {
                    currentUserMeta.category = {}
                }
                currentUserMeta.category[category] = true
            } else {
                userMap.set(user, {
                    annieuser: user,
                    survey: surveyData?.id,
                    meta: {
                        category: {
                            [category] : true
                        }
                    }
                })
            }
        }

        // Remove category from users that are not in selection
        for (let userObj of userMap.values()) {
            if (!selectionSet.has(userObj.annieuser) && userObj?.meta?.category?.[category]) {
                delete userObj.meta.category[category]
            }
        }

        const newUserSurveyData = Array.from(userMap.values())

        setUserSurveyData(newUserSurveyData)

        hidePopup()
    }

    return <><div className={"provider-selection"}>
        <h1>
            <FormattedMessage
                id={"assignSupportTopicTo"}
                defaultMessage={"Assign support topic {topic} to"}
                values={{topic: <b>{topic}</b>}}/>
        </h1>
        <div className={"selected-providers"}>
            <h3>{intl.formatMessage({id:'selectedSupportProviders', defaultMessage: 'Selected support providers:'})} ({selectedProviders?.length})
                <div className={"provider-options"}>
                    {selectedProviders?.length > 0 ?
                        <button className={"text"} onClick={()=> setUsersToCopy()}>
                            {intl.formatMessage(
                                {
                                    id: 'copy',
                                    defaultMessage: 'Copy',
                                })}
                        </button> :
                        <button className={"text"} disabled>
                            {intl.formatMessage(
                                {
                                    id: 'copy',
                                    defaultMessage: 'Copy',
                                })}
                        </button>
                    }
                    {copiedUsersData.length > 0 ?
                        <button className={"text"} onClick={()=> pasteSelections()}>
                            {intl.formatMessage(
                                {
                                    id: 'paste',
                                    defaultMessage: 'Paste',
                                })}
                        </button> :
                        <button className={"text"} disabled>
                            {intl.formatMessage(
                                {
                                    id: 'paste',
                                    defaultMessage: 'Paste',
                                })}
                        </button>
                    }
                    {selectedProviders?.length > 0 ?
                        <button className={"text"} onClick={()=> clearSelections()}>
                            {intl.formatMessage(
                                {
                                    id: 'clear',
                                    defaultMessage: 'Clear',
                                })}
                        </button> :
                        <button className={"text"} disabled>
                            {intl.formatMessage(
                                {
                                    id: 'clear',
                                    defaultMessage: 'Clear',
                                })}
                        </button>
                    }
                </div>
            </h3>
            <div className={"selected-providers-container"}>
                {selectedProviders.map((user, i)=>{
                    return <div className={"provider"} key={i}>
                        {findUserById(user, userData)}
                        <button onClick={()=> removeUserSelection(user)}>
                            <CloseIcon/>
                        </button>
                    </div>
                })}
                {selectedProviders?.length < 1 &&
                    <p className={"placeholder"}>{intl.formatMessage({defaultMessage:'No support providers set.', id:'noProvidersSet'})}</p>
                }
            </div>
        </div>
        <Table columns={columns}
               data={userData}
               globalSearchPlaceholder={globalSearchPlaceholder}
               initialSelections={selectedProviders}
               updateSelectedProviders={(id, state)=>handleProviderUpdate(id, state)} />
        <div className={"modal-options"}>
            <div>
                <button className={"cancel"} onClick={()=> cancelProviderEdits()}>
                    {intl.formatMessage(
                        {
                            id: 'modal.cancel',
                            defaultMessage: 'Cancel',
                        })}
                </button>
                <button onClick={()=> confirmProviderEdits()} className={"confirm"}>
                    {intl.formatMessage(
                        {
                            id: 'select',
                            defaultMessage: 'Select',
                        })}
                </button>
            </div>
        </div>
    </div>
        </>
}

function Line() {
    return (
        <div className="line" />
    )
}

function MessageEditor({ text, placeholder, keyName, borderBottom }) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { setFocus, anyEditorFocused } = useContext(EditorFocusContext)
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"

    const [editorState, setEditorState] = useState(
        () => EditorState.createWithContent(ContentState.createFromText(text)),
    )

    const [editorIsActive, setEditorIsActive] = useState(false)
    const editorContainer = useRef(null)

    //Set variable component and update editor state with it onload
    const decorators = new CompositeDecorator([
        {
            strategy: variableStrategy,
            component: VariableComponent,
            editable: false,
            props: {
                editorState: editorState,
                editorUpdate: e => editorUpdate(e)
            }
        }])
    useEffect(()=>{
        setEditorState(EditorState.set(editorState, {decorator: decorators}))
    },[])

    // Update editor state if content changed elsewhere eg. undo
    useEffect(()=> {
        if (editorState.getCurrentContent().getPlainText() !== text && !editorIsActive) {
            const newContent = ContentState.createFromText(text)
            const newState = EditorState.push(editorState, newContent, 'insert-characters')
            const stateWithDecorators = EditorState.set(newState, {decorator: decorators})
            // Clear editors undo stack
            const clearedHistoryState = EditorState.set(stateWithDecorators, {
                undoStack: EditorState.createEmpty().getUndoStack()
            })

            setEditorState(clearedHistoryState)
        }
    },[text])

    // Instead of debounce, just save every 5 seconds to global state to not overflood it with every update
    useEffect(() => {
        const saveInterval = setInterval(() => {
            if (editorIsActive) saveToContext()
        }, 5000)

        // Clean up timer
        return () => {
            clearInterval(saveInterval)
        }
    }, [editorState])

    const saveToContext = () => {
        const plainText = editorState.getCurrentContent().getPlainText()
        const newSurveyData = {...surveyData}

        if (keyName === "initial") {
            newSurveyData.config.message = plainText
        } else {
            newSurveyData.config[keyName].message = plainText
        }

        setSurveyData(newSurveyData)
    }

    const editorUpdate = e => {
        // update editor with variable decorator
        const editorStateWithDecorators = EditorState.set(e, {decorator: decorators})
        setEditorState(editorStateWithDecorators)
    }

    const handleBlur = () => {
        setFocus(keyName, false)
        saveToContext()
    }

    const pasteVariable = variable => {
        const newState = pasteText(variable, editorState)
        editorUpdate(newState)
    }

    const pasteText = (text, editorValue) => {
        const selection = editorValue.getSelection()
        const contentState = editorValue.getCurrentContent()
        const newContent = Modifier.insertText(contentState, selection, text)
        const newEditorState = EditorState.push(editorValue, newContent, 'insert-characters')
        return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter())
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (editorContainer.current && !editorContainer.current.contains(event.target) ) {
                setEditorIsActive(false)
            }
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        }
    },[editorContainer])

    const handleEditorClick = () => {
        setEditorIsActive(true);
    }

    const handleUndo = () => {
        const undoStack = editorState.getUndoStack()

        if (undoStack.size !== 0) {
            const newEditorState = EditorState.undo(editorState)
            setEditorState(newEditorState)
        }
    }

    const handleKeyCommand = (command) => {
        if (command === 'undo') {
            handleUndo()
            return 'handled'
        }

        // Handle other key commands or let Draft.js handle them
        return 'not-handled'
    }

    return <div className={`${borderBottom ? "border-bottom" : ""} editor ${editorIsActive ? "focused" : ""}`} ref={editorContainer} onClick={handleEditorClick}>
        {surveyFinished ?
        <Editor editorState={editorState} readOnly={true} disabled placeholder={placeholder} stripPastedStyles={true}/> :
            <>
                <Editor
                    editorState={editorState}
                    onChange={editorUpdate}
                    placeholder={placeholder}
                    stripPastedStyles={true}
                    onFocus={() => setFocus(keyName, true)}
                    onBlur={handleBlur}
                    handleKeyCommand={handleKeyCommand}
                />
                {editorIsActive &&
                <div className={"editor-toolbar"}>
                    <AddVariable pasteVariable={variable => pasteVariable(variable)}/>
                    <LengthIndicator content={editorState.getCurrentContent().getPlainText()}/>
                </div>
                }
            </>
    }
    </div>
}

export function LengthIndicator({ content }) {
    const intl = useIntl()
    const textLength = content ? content?.length : 0

    const hasUnicode = content => {
        const GSM_7_BIT_EX = /[\@£\$¥èéùìòÇØøÅåÆæßÉ!\"\#¤%&'\(\)\*\+\,\-\.\/:;<=>\?¡ÄÖÑÜ§¿äöñüà^{}\\\[\]~\|€]/;

        return [...content].some(char => char.charCodeAt(0) > 127 && !GSM_7_BIT_EX.test(char));
    }

    const getMessageState = (hasUnicode, textLength) => {
        if (hasUnicode) {
            if (textLength < 71) {
                return "good";
            } else if (textLength < 135) {
                return "okay";
            } else {
                return "bad";
            }
        } else {
            if (textLength < 161) {
                return "good";
            } else if (textLength < 307) {
                return "okay";
            } else {
                return "bad";
            }
        }
    }

    const messageState = getMessageState(hasUnicode(content), textLength)
    const maxLength = hasUnicode(content) ? 70 : 160

    const tooltipText = hasUnicode(content) ?
        intl.formatMessage({id:'smsLengthTooltip.unicode', defaultMessage:'Emojis reduce the limit to 70 characters (1 Unicode SMS). Keep messages under 70 characters to send a single message (green). Messages between 71-134 characters (2 Unicode SMS) are readable but take multiple messages (yellow). Messages over 134 characters will be split and reduce readability (red).'}) :
        intl.formatMessage({id:'smsLengthTooltip', defaultMessage:'Limit messages to 160 characters (1 SMS) for readability (green). Messages between 161-306 characters (2 SMS) are okay but less readable (yellow). Messages over 306 characters will be split (red).'})

    return <HoverTooltip text={tooltipText}>
        <div className={`${messageState} length-indicator`}>
        {textLength} / {maxLength}
    </div>
    </HoverTooltip>
}

export const VariableComponent = props => {
    const intl = useIntl()
    const { value: surveyData } = useDataContext('surveyData')
    const { value: contactsData } = useDataContext('contactsData')
    const { value: contactMetaArray } = useDataContext('contactMetaArray')

    const optionsWithNames = useMemo(() => contactMetaArray.map(obj => ({
        variable: obj,
        name: intl.formatMessage({ defaultMessage: obj, id: `contactmeta.${obj}` })
    })), [contactMetaArray, intl])
    const namesForDropdown = optionsWithNames.map(obj => obj.name)

    const [showVariableSelector, setShowVariableSelector] = useState(false)
    const variable = props?.decoratedText.slice(2, props?.decoratedText?.length - 2)

    const surveyContactIds = useMemo(() => new Set(surveyData?.contacts), [surveyData])
    const contactsInSurvey = useMemo(() => contactsData.filter(contact => surveyContactIds.has(contact.id)), [contactsData, surveyContactIds])

    const variableIsInUse = useMemo(() => contactMetaArray.includes(variable), [contactMetaArray, variable])
    const allStudentsHaveVariable = useMemo(() => {
        // If there are no contacts in the survey
        if (contactsInSurvey?.length < 1) {
            return false
        }
        return contactsInSurvey.every(contact =>
            contact.meta && variable in contact.meta && contact.meta[variable] != null && contact.meta[variable] !== ""
        )
    }, [contactsInSurvey, variable])

    const variableType = useMemo(() => {
        if (!variableIsInUse) {
            return "variableBroken"
        } else if (allStudentsHaveVariable) {
            return "variableOk"
        } else {
            return "variableMissing"
        }
    }, [variableIsInUse, allStudentsHaveVariable])

    const variableClass = variableType === "variableBroken" ? " bad" :
        variableType === "variableOk" ? " good" : " alert"

    const variableTitle = intl.formatMessage({
        id: `${variableType}Title`,
        defaultMessage: {
            "variableBroken": "Given variable does not exist in student meta.",
            "variableOk": "All students have variable.",
            "variableMissing": "Some students are missing given variable."
        }[variableType]
    });

    const popoverTriggerRef = useRef(null)
    const [toggleWidth, setToggleWidth] = useState(0)
    useEffect(() => {
        if (popoverTriggerRef.current) {
            const rect = popoverTriggerRef.current.getBoundingClientRect()
            setToggleWidth(rect.width)
        }
    }, [variable])

    const addVariable = variable => {
        const foundVariable = optionsWithNames.find(obj => obj.name === variable)
        if (foundVariable) {
            changeVariable(foundVariable?.variable)
        } else {
            changeVariable(variable)
        }
    }

    function changeVariable(variable) {
        const editorState = props?.editorState
        const startIndex = props?.start
        const endIndex = props?.end
        const selection = editorState.getSelection()
        const focusKey = props?.blockKey // to change text from selected line
        const newSelection = selection.merge({
            anchorOffset: startIndex,
            focusOffset: endIndex,
            focusKey: focusKey,
            anchorKey: focusKey
        })
        // Replace old variable with new
        const newContent = Modifier.replaceText(
            editorState.getCurrentContent(),
            newSelection,
            "{{"+variable+"}}"
        )
        // Make selection after replacing variable
        const selectionAfterNewContent = newContent.getSelectionAfter()
        const selectionAfterNewContentAndBrackets = selectionAfterNewContent.merge({
            anchorOffset: selectionAfterNewContent.getAnchorOffset(),
            focusOffset: selectionAfterNewContent.getFocusOffset()
        })
        // Add new editor state to stack and update editor to match
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters')
        const editorStateWithSelection = EditorState.forceSelection(newEditorState, selectionAfterNewContentAndBrackets)
        props?.editorUpdate(editorStateWithSelection)

        setShowVariableSelector(false)
    }

    return <>
        <div className={"variable-container"}>
            <button className={"contact-variable" + variableClass}
                    ref={popoverTriggerRef}
                    title={variableTitle}
                    onClick={()=> {
                        setShowVariableSelector(!showVariableSelector)
                    }}
            >
                <span className={'hidden'}>
                {props.children}
            </span>
            <span contentEditable={false}>
               {intl.formatMessage({
                   defaultMessage: variable,
                   id: `contactmeta.${variable}`
               })}
            </span>
            </button>
            {showVariableSelector &&
                <SearchDropdown options={namesForDropdown}
                                onSelect={variable=>addVariable(variable)}
                                onClose={()=>setShowVariableSelector(false)}
                                toggle={popoverTriggerRef}
                                left={toggleWidth}
                />
            }
        </div>
    </>
}

const variableRegex = /{{[^{{|}}]+}}/g
export function variableStrategy(contentBlock, callback, contentState) {
    findWithRegex(variableRegex, contentBlock, callback)
}

function findWithRegex(regex, contentBlock, callback) {
    const text = contentBlock.getText();
    let matchArr, start;
    while ((matchArr = regex.exec(text)) !== null) {
        start = matchArr.index;
        callback(start, start + matchArr[0].length);
    }
}

export function AddVariable({pasteVariable}) {
    const intl = useIntl()
    const [showDropdown, setShowDropdown] = useState(false)
    const { value: contactsData, setValue: setContactsData } = useDataContext('contactsData')
    const { value: contactMetaArray } = useDataContext('contactMetaArray')

    const dropdownOptions = [...contactMetaArray]
    const optionsWithNames = dropdownOptions.map(obj => {
        return {
            variable: obj,
            name: intl.formatMessage({defaultMessage: obj, id: `contactmeta.${obj}`})}
    })
    const namesForDropdown = optionsWithNames.map(obj => obj.name)

    const addVariable = variable => {
        const foundVariable = optionsWithNames.find(obj => obj.name === variable)
        if (foundVariable) {
            pasteVariable("{{"+foundVariable?.variable+"}}")
        } else {
            pasteVariable("{{"+variable+"}}")
        }
        setShowDropdown(false)
    }
    const variable = <VariableIcon />
    const [toggleWidth, setToggleWidth] = useState(0)
    const dropdownToggle = useRef(null)

    useEffect(() => {
        if (dropdownToggle.current) {
            const rect = dropdownToggle.current.getBoundingClientRect()
            setToggleWidth(rect.width)
        }
    }, [])

    return <div className={"add-variable-container"}>
        <button ref={dropdownToggle} onClick={()=> {setShowDropdown(!showDropdown)}}
                title={"+ "+intl.formatMessage({id:"addVariable",defaultMessage:"Add variable"})}>
            {variable}
            <span>{intl.formatMessage({id:"addVariable",defaultMessage:"Add variable"})}</span>
        </button>
        {showDropdown &&
            <SearchDropdown options={namesForDropdown}
                            onSelect={variable=>addVariable(variable)}
                            onClose={()=>setShowDropdown(false)}
                            toggle={dropdownToggle}
                            left={toggleWidth + 10}
            />
        }
    </div>
}